import type { FC, ReactNode } from 'react'
import type { Job } from '@/types'
import type { VehicleFilters } from '@/components/jobs/vehicle-filter-sheet'

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import {
    CarFront,
    Plus,
    Search,
    SlidersHorizontal,
    X
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Fab } from '@/components/app/fab'
import { DataGate } from '@/components/app/data-gate'
import { PageContainer, PageHeader } from '@/components/app/page-header'
import { Button } from '@/components/kit/button'
import { Input } from '@/components/kit/field'
import { Segmented } from '@/components/kit/segmented'
import { EmptyState } from '@/components/kit/empty-state'
import { JobRow } from '@/components/jobs/job-row'
import { VehicleFilterSheet } from '@/components/jobs/vehicle-filter-sheet'
import { useNow } from '@/hooks/use-now'
import { useCarWashData } from '@/hooks/use-carwash'
import { isActiveJob } from '@/lib/carwash/engine'
import { dayKey, formatDayLabel, formatMoney } from '@/lib/carwash/format'
import { normalizePlate } from '@/lib/carwash/plate'
import { digitsOnly } from '@/lib/carwash/phone'

import {
    describeDateFilter,
    inDateRange,
    isDateFilter,
    resolveDateFilter
} from '@/lib/carwash/ranges'

type VehicleTab = 'all' | 'waiting' | 'washing' | 'completed'

const TABS: VehicleTab[] = ['all', 'waiting', 'washing', 'completed']

const matchesTab = (job: Job, tab: VehicleTab): boolean => {
    if (tab === 'all') return true
    if (tab === 'waiting') return job.status === 'waiting'
    if (tab === 'washing') return job.status === 'assigned' || job.status === 'washing' || job.status === 'quality_check'
    return job.status === 'completed'
}

const PAGE_SIZE = 40

const VehiclesView: FC = (): ReactNode => {
    const router = useRouter()
    const { state, lookups } = useCarWashData()
    const now = useNow(60_000)

    const [tab, setTab] = useState<VehicleTab>('all')
    const [query, setQuery] = useState('')
    const [filters, setFilters] = useState<VehicleFilters>({ date: 'today', service: 'all' })
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [limit, setLimit] = useState(PAGE_SIZE)
    const [ready, setReady] = useState(false)

    /* Restore the view from the address, so links and the back button land on the same list. */
    useEffect(() => {
        if (!router.isReady || ready) return

        const { tab: qTab, date, service, q } = router.query

        if (typeof qTab === 'string' && TABS.includes(qTab as VehicleTab)) setTab(qTab as VehicleTab)
        if (typeof q === 'string') setQuery(q)

        setFilters({
            date: typeof date === 'string' && isDateFilter(date) ? date : 'today',
            service: typeof service === 'string' ? service : 'all'
        })

        setReady(true)
    }, [router.isReady, router.query, ready])

    useEffect(() => {
        if (!ready) return

        const next: Record<string, string> = {}
        if (tab !== 'all') next.tab = tab
        if (filters.date !== 'today') next.date = filters.date
        if (filters.service !== 'all') next.service = filters.service
        if (query.trim()) next.q = query.trim()

        void router.replace({ pathname: '/vehicles', query: next }, undefined, { shallow: true, scroll: false })
        // router is stable enough here; only the view state drives this.
    }, [tab, filters, query, ready]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setLimit(PAGE_SIZE)
    }, [tab, filters, query])

    const currency = state.settings.currency
    const range = resolveDateFilter(filters.date, now)
    const needle = query.trim().toLowerCase()
    const plateNeedle = normalizePlate(query)
    const digitNeedle = digitsOnly(query)

    const includesNow = !range || (now >= range.start && now < range.end)

    const base = useMemo(() => state.jobs.filter(job => {
        const current = includesNow && isActiveJob(job)
        if (!current && !inDateRange(job.enteredAt, range)) return false

        if (filters.service !== 'all') {
            if (filters.service === 'custom' ? job.serviceId !== null : job.serviceId !== filters.service) return false
        }

        if (needle) {
            const customer = job.customerId ? lookups.customers.get(job.customerId) : null
            const plateHit = plateNeedle.length > 0 && job.plate.includes(plateNeedle)
            const modelHit = (job.vehicleModel ?? '').toLowerCase().includes(needle)
            const nameHit = (customer?.name ?? '').toLowerCase().includes(needle)
            const phoneHit = digitNeedle.length >= 4 && digitsOnly(customer?.phone).includes(digitNeedle)
            const numberHit = `#${job.number}`.includes(needle)

            if (!plateHit && !modelHit && !nameHit && !phoneHit && !numberHit) return false
        }

        return true
    }), [state.jobs, range?.start, range?.end, includesNow, filters.service, needle, plateNeedle, digitNeedle, lookups]) // eslint-disable-line react-hooks/exhaustive-deps

    const counts = useMemo(() => Object.fromEntries(TABS.map(item => [item, base.filter(job => matchesTab(job, item)).length])) as Record<VehicleTab, number>, [base])

    const visible = useMemo(() => {
        const list = base.filter(job => matchesTab(job, tab))

        return list.sort((a, b) => tab === 'waiting'
            ? a.enteredAt.localeCompare(b.enteredAt)
            : b.enteredAt.localeCompare(a.enteredAt))
    }, [base, tab])

    const shown = visible.slice(0, limit)

    const groups = useMemo(() => {
        const map = new Map<string, Job[]>()

        for (const job of shown) {
            const key = dayKey(job.enteredAt)
            const list = map.get(key)
            if (list) list.push(job)
            else map.set(key, [job])
        }

        return Array.from(map.entries())
    }, [shown])

    const filterCount = (filters.date !== 'today' ? 1 : 0) + (filters.service !== 'all' ? 1 : 0)
    const serviceLabel = filters.service === 'all'
        ? null
        : filters.service === 'custom' ? 'Custom services' : lookups.services.get(filters.service)?.name ?? 'Service'

    return (
        <>
            <PageHeader
                title='Vehicles'
                subtitle={`${describeDateFilter(filters.date, now)}${serviceLabel ? `, ${serviceLabel}` : ''}`}
                actions={(
                    <Button
                        variant='secondary'
                        size='icon'
                        onClick={() => setFiltersOpen(true)}
                        aria-label={filterCount ? `Filters, ${filterCount} active` : 'Filters'}
                        className='relative'
                    >
                        <SlidersHorizontal aria-hidden='true' />

                        {filterCount > 0 && (
                            <span className='absolute -top-1 -right-1 grid size-5 place-items-center rounded-full bg-primary text-[11px] font-bold text-white'>
                                {filterCount}
                            </span>
                        )}
                    </Button>
                )}
            >
                <div className='relative'>
                    <label htmlFor='vehicle-search' className='sr-only'>
                        Search by vehicle number
                    </label>

                    <Input
                        id='vehicle-search'
                        type='search'
                        inputMode='search'
                        autoComplete='off'
                        autoCapitalize='characters'
                        enterKeyHint='search'
                        placeholder='Search vehicle number, model or customer'
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        leading={<Search className='size-5' aria-hidden='true' />}
                        trailing={query
                            ? (
                                <button
                                    type='button'
                                    onClick={() => setQuery('')}
                                    aria-label='Clear search'
                                    className='grid size-9 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                                >
                                    <X className='size-4' aria-hidden='true' />
                                </button>
                            )
                            : undefined}
                        className='h-11 bg-white text-[15px]'
                    />
                </div>
            </PageHeader>

            <PageContainer className='space-y-4'>
                <Segmented
                    label='Vehicle status'
                    value={tab}
                    onChange={setTab}
                    options={[
                        { value: 'all', label: 'All', count: counts.all },
                        { value: 'waiting', label: 'Waiting', count: counts.waiting },
                        { value: 'washing', label: 'Washing', count: counts.washing },
                        { value: 'completed', label: 'Completed', count: counts.completed }
                    ]}
                />

                {filterCount > 0 && (
                    <div className='flex flex-wrap items-center gap-2 text-[13px]'>
                        <span className='font-medium text-muted-foreground'>
                            Showing
                        </span>

                        {filters.date !== 'today' && (
                            <span className='rounded-full bg-accent px-2.5 py-1 font-semibold text-accent-foreground'>
                                {describeDateFilter(filters.date, now)}
                            </span>
                        )}

                        {serviceLabel && (
                            <span className='rounded-full bg-accent px-2.5 py-1 font-semibold text-accent-foreground'>
                                {serviceLabel}
                            </span>
                        )}

                        <button
                            type='button'
                            onClick={() => setFilters({ date: 'today', service: 'all' })}
                            className='font-semibold text-primary hover:underline'
                        >
                            Clear
                        </button>
                    </div>
                )}

                {groups.length === 0
                    ? (
                        <EmptyState
                            icon={<CarFront />}
                            title={needle ? 'No vehicles match your search' : 'No vehicles here yet'}
                            description={needle
                                ? 'Check the number, or widen the date filter to search older visits.'
                                : filters.date === 'today' ? 'Vehicles registered today will appear here.' : 'Try another date or service.'}
                            action={needle && filters.date !== 'all'
                                ? (
                                    <Button variant='secondary' onClick={() => setFilters(current => ({ ...current, date: 'all' }))}>
                                        Search all dates
                                    </Button>
                                )
                                : undefined}
                        />
                    )
                    : groups.map(([key, jobs]) => {
                        const billed = jobs.reduce((sum, job) => {
                            const invoice = job.invoiceId ? lookups.invoices.get(job.invoiceId) : null
                            return sum + (invoice ? invoice.total : 0)
                        }, 0)

                        return (
                            <section key={key} aria-label={formatDayLabel(jobs[0].enteredAt, now)}>
                                <div className='mb-2 flex items-baseline justify-between gap-3 px-1'>
                                    <h2 className='text-[13px] font-bold tracking-wide text-slate-600 uppercase'>
                                        {formatDayLabel(jobs[0].enteredAt, now)}
                                    </h2>

                                    <p className='text-xs font-medium text-muted-foreground tabular-nums'>
                                        {`${jobs.length} ${jobs.length === 1 ? 'vehicle' : 'vehicles'}${billed ? `, ${formatMoney(billed, currency)}` : ''}`}
                                    </p>
                                </div>

                                <div className='grid gap-2 lg:grid-cols-2'>
                                    {jobs.map(job => (
                                        <JobRow
                                            key={job.id}
                                            job={job}
                                            staff={job.staffId ? lookups.staff.get(job.staffId) ?? null : null}
                                            invoice={job.invoiceId ? lookups.invoices.get(job.invoiceId) ?? null : null}
                                            currency={currency}
                                        />
                                    ))}
                                </div>
                            </section>
                        )
                    })}

                {visible.length > shown.length && (
                    <Button variant='secondary' size='lg' className='w-full' onClick={() => setLimit(value => value + PAGE_SIZE)}>
                        {`Show more (${visible.length - shown.length} left)`}
                    </Button>
                )}
            </PageContainer>

            <Fab href='/vehicles/new' label='New' icon={<Plus aria-hidden='true' />} />

            <VehicleFilterSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                value={filters}
                onApply={setFilters}
                services={state.services}
                hasCustom={state.jobs.some(job => job.serviceId === null)}
            />
        </>
    )
}

const VehiclesPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='Vehicles' description='Every vehicle in the wash and its full history, searchable by number plate.' />

            <DataGate>
                <VehiclesView />
            </DataGate>
        </Layout>
    )
}

export default VehiclesPage
